import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Box,
  FormControl,
  IconButton,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

// Components
import { useNotification } from "../utils/Notification";
import { trackEvent } from "../utils/Utils";
import Header from "./Header";

// Context
import { useAuth } from "../../contexts/AuthContext";

// Supabase
import supabase from "../../supabaseClient";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";

const Profile = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const [profile, setProfile] = useState({
    email: "",
    first_name: "",
    last_name: "",
    nickname: "",
    countrycode_id: "",
    phone: "",
    locale: "",
    stripe_id: "",
  });

  const [countryCodes, setCountryCodes] = useState([]);
  const [variables, setVariables] = useState({});
  const [newVariableName, setNewVariableName] = useState("");
  const [newVariableValue, setNewVariableValue] = useState("");
  const [password, setPassword] = useState("");
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // Context
  const { user, signOut, setNewPassword } = useAuth();
  const { showNotification } = useNotification();

  // Hooks
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfileData = async () => {
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select(
          `
              id,
              account_id(
                email,
                locale,
                team_id(
                  stripe_id
                )
              ),
              first_name,
              last_name,
              nickname,
              countrycode_id,
              phone,
              variables
            `
        )
        .single();

      if (profileError) {
        showNotification(
          `${t("profile.errorFetchingProfile")}: ${profileError.message}`,
          "error"
        );
      } else {
        setProfile({
          email: profileData.account_id.email,
          first_name: profileData.first_name,
          last_name: profileData.last_name,
          nickname: profileData.nickname,
          countrycode_id: profileData.countrycode_id,
          phone: profileData.phone,
          locale: profileData.account_id.locale,
          stripe_id: profileData.account_id.team_id.stripe_id,
        });
        setVariables(profileData.variables || {});
      }
    };

    const fetchCountryCodes = async () => {
      const { data, error } = await supabase
        .from("countrycodes")
        .select("*")
        .order("country_iso", { ascending: true });

      if (error) {
        showNotification(
          `${t("profile.errorFetchingCountryCodes")}: ${error.message}`,
          "error"
        );
      } else {
        setCountryCodes(data);
      }
    };

    fetchProfileData();
    fetchCountryCodes();
  }, []);

  const handleProfileChange = (field, value) => {
    setProfile((prevProfile) => ({ ...prevProfile, [field]: value }));
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/signin");
    trackEvent("profile-signed-out");
  };

  const handleVariableChange = (key, value) => {
    setVariables((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (password && user) {
        // First check that length is 6 or more:
        if (password.length < 6) {
          showNotification(t("profile.passwordTooShort"), "error");
          return;
        }

        const { error } = await setNewPassword(password);
        if (error) {
          showNotification(
            `${t("profile.errorUpdatingPassword")}: ${error.message}`,
            "error"
          );
          return;
        }
      }

      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          first_name: profile.first_name,
          last_name: profile.last_name,
          nickname: profile.nickname,
          countrycode_id: profile.countrycode_id,
          phone: profile.phone,
          variables: variables,
        })
        .eq("account_id", user.id);

      if (profileError) {
        throw profileError;
      }

      const { error: accountError } = await supabase
        .from("accounts")
        .update({
          locale: profile.locale,
        })
        .eq("user_id", user.id);

      if (accountError) {
        throw accountError;
      }

      // Set language on Stripe:
      const body = {
        customerId: profile.stripe_id,
        newLocale: profile.locale,
      };

      const stripeResponse = await fetch(
        "https://stripeupdatelocale-tiph5o4x3q-uc.a.run.app",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );
      if (!stripeResponse.ok) {
        throw new Error(
          t("profile.stripeResponseError", { status: stripeResponse.status })
        );
      }

      i18n.changeLanguage(profile.locale);

      trackEvent("profile-changes_saved");
      showNotification(t("profile.successfulUpdate"), "success");
    } catch (error) {
      showNotification(
        `${t("profile.errorUpdatingAccount")}: ${error.message}`,
        "error"
      );
    }
  };

  const handleAddVariable = async () => {
    if (newVariableName && newVariableValue) {
      try {
        const formattedVariableName = newVariableName
          .toLowerCase()
          .replace(/ /g, "_");

        const updatedVariables = {
          ...variables,
          [formattedVariableName]: newVariableValue.trim(),
        };

        const { error } = await supabase
          .from("profiles")
          .update({
            variables: updatedVariables,
          })
          .eq("account_id", user.id);

        if (error) {
          throw error;
        }
        setVariables(updatedVariables);
        setNewVariableName("");
        setNewVariableValue("");
        trackEvent("profile-variable_added", {
          variable: formattedVariableName,
        });
      } catch (error) {
        console.error(t("profile.errorAddingVariable"), error);
        showNotification(t("profile.errorAddingVariable"));
      }
    } else {
      showNotification(t("profile.completeBothFields"), "warning");
    }
  };

  const handleDeleteVariable = async (variableName) => {
    const updatedVariables = { ...variables };
    delete updatedVariables[variableName]; // Remove the variable by name

    setVariables(updatedVariables);
    await supabase
      .from("profiles")
      .update({
        variables: updatedVariables,
      })
      .eq("account_id", user.id);
  };
  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: "1200px", mx: "auto" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Header title={t("profile.title")} subtitle={t("profile.subtitle")} />
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{
          bgcolor: theme.palette.mode === 'dark' ? "rgba(20, 27, 45, 0.7)" : "#ffffff",
          backdropFilter: "blur(10px)",
          borderRadius: "16px",
          boxShadow: theme.palette.mode === 'dark' ? "0 8px 32px 0 rgba(0, 0, 0, 0.3)" : "0 8px 32px 0 rgba(31, 38, 135, 0.05)",
          border: theme.palette.mode === 'dark' ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
          p: { xs: 3, md: 5 },
        }}
      >
        <Typography variant="h4" fontWeight="600" mb={3} sx={{ color: theme.palette.mode === 'dark' ? "#fff" : "#141b2d" }}>
          {t("profile.personalInfo", "Personal Information")}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              label={t("profile.firstName")}
              value={profile.first_name}
              onChange={(e) => handleProfileChange("first_name", e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              label={t("profile.lastName")}
              value={profile.last_name}
              onChange={(e) => handleProfileChange("last_name", e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              label={t("profile.nickname")}
              value={profile.nickname}
              onChange={(e) => handleProfileChange("nickname", e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}>
              <InputLabel id="countrycode_id-label">{t("profile.countryCode")}</InputLabel>
              <Select
                labelId="countrycode_id-label"
                id="countrycode_id"
                value={profile.countrycode_id}
                label={t("profile.countryCode")}
                onChange={(e) => handleProfileChange("countrycode_id", e.target.value)}
              >
                {countryCodes.map((code) => (
                  <MenuItem key={code.country_iso} value={code.country_iso}>
                    {`${code.country_iso} (${code.country_code})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              label={t("profile.phone")}
              type="number"
              value={profile.phone}
              onChange={(e) => handleProfileChange("phone", e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              label={t("profile.email")}
              value={profile.email}
              disabled
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}>
              <InputLabel id="locale-label">{t("profile.language")}</InputLabel>
              <Select
                labelId="locale-label"
                id="locale"
                value={profile.locale}
                label={t("profile.language")}
                onChange={(e) => handleProfileChange("locale", e.target.value)}
              >
                <MenuItem value="en-US">English</MenuItem>
                <MenuItem value="es-MX">Español</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              id="password"
              label={t("profile.password")}
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </Grid>
        </Grid>

        {/* Variables Section */}
        <Box sx={{ mt: 6, pt: 4, borderTop: theme.palette.mode === 'dark' ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)" }}>
          <Typography
            variant="h4"
            fontWeight="600"
            sx={{ mb: 3, display: "flex", alignItems: "center", color: theme.palette.mode === 'dark' ? "#fff" : "#141b2d" }}
          >
            {t("profile.variablesTitle")}
            <Tooltip
              title={<Typography variant="body2">{t("profile.variablesDescription")}</Typography>}
              open={tooltipOpen}
              onClose={() => setTooltipOpen(false)}
              disableHoverListener
            >
              <IconButton size="small" sx={{ ml: 1, color: theme.palette.mode === 'dark' ? "#a1a4ab" : "#727681" }} onClick={() => setTooltipOpen(true)}>
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>

          <Grid container spacing={2}>
            {Object.entries(variables).map(([name, value]) => (
              <Grid item xs={12} sm={6} key={name}>
                <Box sx={{ display: "flex", alignItems: "center", bgcolor: theme.palette.mode === 'dark' ? "rgba(0,0,0,0.2)" : "#f8f9fa", p: 1, borderRadius: "8px" }}>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteVariable(name)}
                    sx={{ color: "#e2726e", mr: 1 }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                  <TextField
                    fullWidth
                    variant="standard"
                    label={name}
                    value={value}
                    onChange={(e) => handleVariableChange(name, e.target.value)}
                    InputProps={{ disableUnderline: true }}
                    sx={{ "& .MuiInputBase-root": { mt: 2 } }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Add New Variable Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mt: 4,
              p: 3,
              bgcolor: theme.palette.mode === 'dark' ? "rgba(0,0,0,0.2)" : "#f8f9fa",
              borderRadius: "12px"
            }}
          >
            <TextField
              label={t("profile.newVariableName")}
              value={newVariableName}
              onChange={(e) => setNewVariableName(e.target.value)}
              fullWidth
              variant="outlined"
              sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: theme.palette.mode === 'dark' ? "rgba(255,255,255,0.05)" : "#fff" } }}
            />
            <TextField
              label={t("profile.newVariableValue")}
              value={newVariableValue}
              onChange={(e) => setNewVariableValue(e.target.value)}
              fullWidth
              variant="outlined"
              sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: theme.palette.mode === 'dark' ? "rgba(255,255,255,0.05)" : "#fff" } }}
            />
            <Button
              variant="contained"
              onClick={handleAddVariable}
              sx={{
                height: "56px",
                minWidth: { xs: "100%", sm: "auto" },
                borderRadius: "8px",
                background: "linear-gradient(90deg, #4cceac 0%, #3da58a 100%)",
                color: "#141b2d",
                fontWeight: "bold",
                boxShadow: "0 4px 14px 0 rgba(76, 206, 172, 0.39)",
                "&:hover": { background: "linear-gradient(90deg, #3da58a 0%, #2e7c67 100%)", color: "#fff" }
              }}
            >
              {t("profile.addVariable")}
            </Button>
          </Box>
        </Box>

        <Box display="flex" gap={2} sx={{ mt: 5, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            color="error"
            onClick={handleSignOut}
            sx={{ borderRadius: "8px", px: 4, py: 1.5, fontWeight: "bold", borderWidth: "2px", "&:hover": { borderWidth: "2px" } }}
          >
            {t("profile.signOut")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              borderRadius: "8px",
              px: 4,
              py: 1.5,
              background: "linear-gradient(90deg, #7949E7 0%, #9A67ED 100%)",
              color: "#fff",
              fontWeight: "bold",
              boxShadow: "0 4px 14px 0 rgba(121, 73, 231, 0.39)",
              "&:hover": { background: "linear-gradient(90deg, #6B53BB 0%, #7949E7 100%)" }
            }}
          >
            {t("profile.saveChanges")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Profile;
